import { Box, Button, Flex, Image, Text, VStack, useBreakpoint } from "@chakra-ui/react";
import dynamic from "next/dynamic";
import { useMemo, useState } from "react";

import type { EventsCoreDataFragment } from "~/__generated__/graphql";
import { getSdk } from "~/__generated__/graphql";
import client from "~/config/client";

import AnimateWrapper from "../animate-wrapper/animate-fade-in-out";
import { LargeBreak, MediumBreak, SmallBreak } from "../landing/v2/helpers";
import SimplePagination from "../pagination/simple-pagination";

const MinimalPagination = dynamic(() => import("../pagination/minimal-pagination"), { ssr: false });

export const getPastEvents = async ({ page, pageSize }: { page: number; pageSize: number }) => {
  const sdk = getSdk(client);

  const events = await sdk.Events({
    pagination: { page, pageSize },
    filters: { eventDate: { lt: new Date() } },
  });

  return { events: events.events?.data, meta: events.events?.meta };
};
const PastEvents = () => {
  const [page, setPage] = useState({ pageSize: 2, page: 1 });
  const [total, setTotal] = useState(0);
  const breakpoint = useBreakpoint();
  const [pastEvents, setPastEvents] = useState<Array<EventsCoreDataFragment[]> | undefined>([]);

  useMemo(async () => {
    await getPastEvents({ page: page.page, pageSize: page.pageSize }).then(({ events, meta }) => {
      setPastEvents((prev) => prev?.concat([events ?? []]));

      setTotal(meta?.pagination.pageCount ?? 0);
    });
  }, [page]);

  if (uniqueArrays(pastEvents ?? []).length === 0 || uniqueArrays(pastEvents ?? [])[0].length === 0)
    return null;
  const isMobile = breakpoint === "base" || breakpoint === "md";
  return (
    <Box w="full" bgColor="rgba(240, 237, 236, 0.2)">
      <Flex
        direction={"column"}
        py={{ xl: "100px", lg: "80px", base: "64px" }}
        px={{ xl: "160px", lg: "24px", base: "16px" }}
        maxWidth={"1440px"}
        mx="auto"
      >
        <Flex
          direction="column"
          align="center"
          mb={{ xl: "80px", lg: "64px", base: "40px" }}
          textAlign={"center"}
        >
          <Text
            fontSize={{ lg: "18px", base: "14px" }}
            color="rgba(13, 48, 80, 0.8)"
            lineHeight={{ lg: "28px", base: "14px" }}
            letterSpacing={"0.05em"}
            mb={{ lg: "16px", base: "12px" }}
          >
            PAST EVENTS
          </Text>
          <Text
            fontSize={{ lg: "56px", base: "32px" }}
            color="#0D3050"
            lineHeight={{ lg: "64px", base: "36px" }}
            fontFamily={"Georgia"}
            mb={{ lg: "24px", base: "16px" }}
          >
            Check Out Our Past Event Highlights!
          </Text>
          <Text
            fontSize={{ lg: "20px", base: "16px" }}
            color="rgba(13, 48, 80, 0.8)"
            lineHeight={{ lg: "32px", base: "24px" }}
            textAlign={"center"}
          >
            Can&apos;t make it to our community events? Check out our past event <LargeBreak />{" "}
            <MediumBreak />
            videos and see <SmallBreak /> what you&apos;re missing!
          </Text>
        </Flex>
        <Box height={{ lg: "656px", base: "940px" }} position="relative">
          {uniqueArrays(pastEvents ?? [])?.map((past, idx) => {
            return (
              <AnimateWrapper
                key={idx}
                activeIndex={page.page - 1}
                defaultIndex={idx}
                isPrev={page.page - 1 > idx}
              >
                <Flex
                  key={idx}
                  align={"start"}
                  direction={{ lg: "row", base: "column" }}
                  gap={{ xl: "40px", lg: "24px" }}
                  position={"absolute"}
                >
                  {past.map((event: EventsCoreDataFragment, idx1: number) => {
                    return (
                      <VStack
                        align={"start"}
                        maxWidth={{ xl: "540px", lg: "477px", base: "343px" }}
                        flex={1}
                        key={idx1}
                        height={"656px"}
                      >
                        <Image
                          src={event.attributes?.thumbnail?.data?.attributes?.url}
                          alt=""
                          w={"full"}
                          height={{ lg: "280px", base: "193px" }}
                          mb={{ lg: "32px", base: "24px" }}
                          mt={{ base: "40px" }}
                          objectFit={"contain"}
                        />
                        <Text
                          fontSize={{ lg: "32px", base: "24px" }}
                          color="#0D3050"
                          lineHeight={{ lg: "44px", base: "32px" }}
                          fontWeight={600}
                          noOfLines={3}
                          mb={{ lg: "8px", base: "12px" }}
                          dangerouslySetInnerHTML={{ __html: event.attributes?.title ?? "" }}
                        />

                        <Text
                          fontSize={{ lg: "18px", base: "16px" }}
                          color="rgba(13, 48, 80, 0.8)"
                          lineHeight={{ lg: "28px", base: "24px" }}
                          noOfLines={3}
                          dangerouslySetInnerHTML={{ __html: event.attributes?.description ?? "" }}
                        />

                        <Flex
                          justify={"start"}
                          pt={{ lg: "24px", base: "20px" }}
                          width={{ lg: "194px", base: "full" }}
                        >
                          <Button
                            w={{ lg: "121px", base: "full" }}
                            color="#0D3050"
                            onClick={() =>
                              window.open(event.attributes?.youtubeURL as string, "_blank")
                            }
                          >
                            Watch now
                          </Button>
                        </Flex>
                      </VStack>
                    );
                  })}
                </Flex>
              </AnimateWrapper>
            );
          })}
        </Box>
        <Box pt={{ xl: "84px", lg: "64px", base: "40px" }} w="full">
          {!isMobile && (
            <MinimalPagination
              variant="minimal"
              page={page.page}
              pageSize={page.pageSize}
              count={Math.ceil(total * page.pageSize)}
              hasLegend={false}
              hasPageControls
              onChange={function (pageData) {
                setPage(pageData);
              }}
            />
          )}

          {isMobile && (
            <SimplePagination
              page={page.page}
              count={total}
              onPageChange={(newPage) => setPage({ ...page, page: newPage })}
            />
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default PastEvents;
function uniqueArrays(arrays: EventsCoreDataFragment[][]) {
  const stringifiedArrays = new Set(arrays.map((array) => JSON.stringify(array)));
  return Array.from(stringifiedArrays).map((arrayString) => JSON.parse(arrayString));
}
